import React from 'react';
import styled from "styled-components";
import PreEFormMappingHeader from '../../PreEFormFields/PreEFormMappingHeader';
import PreEFormMapping from "../../../PreEForm/PreEFormMapping";

const CheckBoxContainer = styled.div`
    overflow-x: hidden;
    padding-left: 0px;
    border: 1px solid grey
`;

/**
 * IPSLocations
 *
 * Unified component for rendering IPS location mappings with asset checkboxes.
 * Works in both standard form view and interactive viewer/attestation mode.
 *
 * Props
 * ─────
 * preEForm              – current form object
 * preEFormMappings      – array of IPS location mapping objects
 * assets                – (legacy, unused here)
 * onChangeAssetId       – called when an individual asset checkbox toggles
 * selectAllAssets       – called when the location-level "select all" checkbox toggles
 * saveVisibleText       – legacy: in non-viewer mode, falsy = enabled (inverted)
 *                         in viewer mode, truthy = enabled (standard)
 * attestView            – true when in viewer/attestation mode; enables:
 *                           • asset filtering by selectableAssetIds
 *                           • viewer highlight callbacks
 *                           • alternative disable logic
 * disabled              – explicit disable flag (viewer mode only)
 * readOnly              – alias for disabled (viewer mode only)
 * selectableAssetIds    – (attestView only) Set<string> of ipsAssetId values
 *                         that resolved to a model node. Assets not in this
 *                         set are hidden. Empty Set = show all (model loading).
 * onSelectionChange     – (attestView only) scrollable-list selection callback
 * onAssetCheckboxChange – (attestView only) individual checkbox callback for
 *                         viewer highlighting
 */
const IPSLocations = ({
  preEForm,
  preEFormMappings,
  assets,
  onChangeAssetId,
  selectAllAssets,
  saveVisibleText,
  attestView = false,
  disabled = false,
  readOnly = false,
  selectableAssetIds = null,
  onSelectionChange,
  onAssetCheckboxChange,
}) => {
  // ── Interaction gate ─────────────────────────────────────────────────────
  // Determine if checkboxes should be disabled.
  //
  // In attestView (viewer) mode:
  //   - Use explicit disabled/readOnly flags
  //   - saveVisibleText works normally (truthy = enabled)
  //
  // In standard form mode (attestView=false):
  //   - Legacy inverted semantics: !saveVisibleText means enabled
  //   - This preserves backwards compatibility with existing form usage
  const isDisabled = attestView
    ? (disabled || readOnly || !saveVisibleText)
    : !saveVisibleText;  // inverted for legacy compatibility

  // ── Asset filtering (attestView only) ────────────────────────────────────
  // Only filter when:
  //   1. attestView is true (viewer / attestation context)
  //   2. selectableAssetIds is a non-empty Set (model has finished loading)
  const shouldFilter =
    attestView &&
    selectableAssetIds instanceof Set &&
    selectableAssetIds.size > 0;

  const visibleMappings = shouldFilter
    ? (preEFormMappings ?? []).reduce((acc, mapping) => {
        const selectableAssets = (mapping.assets ?? []).filter((asset) => {
          const id = String(asset.ipsAssetId ?? '').trim();
          return id && selectableAssetIds.has(id);
        });
        // Only include this location if it has at least one selectable asset.
        // Shallow-clone so the original mapping data is never mutated.
        if (selectableAssets.length > 0) {
          acc.push({ ...mapping, assets: selectableAssets });
        }
        return acc;
      }, [])
    : (preEFormMappings ?? []);

  return (
    <CheckBoxContainer>
      <PreEFormMappingHeader />
      {visibleMappings.length > 0
        ? visibleMappings.map(preEFormMapping =>
            <PreEFormMapping
              key={preEFormMapping.ipsLocationId}
              ipsLocationId={preEFormMapping.ipsLocationId}
              onChangeAssetId={onChangeAssetId}
              selectAllAssets={selectAllAssets}
              preEFormMapping={preEFormMapping}
              currentFormId={preEForm?.id}
              saveVisibleText={isDisabled}
              disabled={isDisabled}
              attestView={attestView}
              onAssetCheckboxChange={attestView ? onAssetCheckboxChange : undefined}
            />
          )
        : shouldFilter && (
            <div style={{ padding: '12px', color: '#888', fontSize: '13px' }}>
            </div>
          )
      }
    </CheckBoxContainer>
  );
};

export default IPSLocations;
